import React, { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Progress } from '../../components/ui/progress';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  FileText,
  ChevronRight,
  ChevronLeft,
  CheckCircle,
  Upload,
  MapPin,
  Calendar,
  Package,
  Truck,
  DollarSign,
  Clock,
  Users,
  Star,
  Shield,
  MessageSquare,
  Search,
  Filter,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ==================== RFQ CREATION WIZARD ====================

export function RFQWizard({ 
  open, 
  onOpenChange, 
  subcategory,
  country = 'UK',
  onComplete,
}) {
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [rfqData, setRfqData] = useState({
    // Step 0: Basics
    title: '',
    subcategory_slug: subcategory?.slug || '',
    country: country,
    delivery_location: '',
    needed_by: '',
    
    // Step 1: Specs
    item_details: '',
    product_type: '',
    brand_preference: '',
    quantity: '',
    quantity_unit: 'pcs',
    moq_acceptable: true,
    packaging: '',
    
    // Step 2: Delivery
    delivery_needed: true,
    delivery_address: '',
    pickup_preference: false,
    
    // Step 3: Budget
    budget_range: '',
    best_price_toggle: false,
    attachments: [],
    
    // Contact
    contact_name: '',
    contact_email: '',
    contact_phone: '',
  });
  const [rfqNumber, setRfqNumber] = useState('');

  const currencySymbol = country === 'UK' ? '£' : 'Rs.';

  const steps = [
    { title: 'Basics', icon: FileText },
    { title: 'Specifications', icon: Package },
    { title: 'Delivery', icon: Truck },
    { title: 'Budget & Submit', icon: DollarSign },
  ];

  const updateData = (field, value) => {
    setRfqData(prev => ({ ...prev, [field]: value }));
  };

  const canProceed = () => {
    switch (step) {
      case 0:
        return rfqData.title && rfqData.delivery_location;
      case 1:
        return rfqData.item_details && rfqData.quantity;
      case 2:
        return rfqData.delivery_needed ? rfqData.delivery_address : true;
      case 3:
        return rfqData.contact_name && rfqData.contact_email && rfqData.contact_phone;
      default:
        return true;
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/business-services/rfq`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(rfqData),
      });

      const data = await response.json();

      if (data.success) {
        setRfqNumber(data.rfq_number);
        setStep(4); // Success step
        toast.success('RFQ submitted successfully!');
        onComplete?.(data);
      } else {
        toast.error(data.detail || 'Failed to submit RFQ');
      }
    } catch (error) {
      console.error('Error submitting RFQ:', error);
      toast.error('Failed to submit RFQ');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setStep(0);
    setRfqData({
      title: '',
      subcategory_slug: subcategory?.slug || '',
      country: country,
      delivery_location: '',
      needed_by: '',
      item_details: '',
      product_type: '',
      brand_preference: '',
      quantity: '',
      quantity_unit: 'pcs',
      moq_acceptable: true,
      packaging: '',
      delivery_needed: true,
      delivery_address: '',
      pickup_preference: false,
      budget_range: '',
      best_price_toggle: false,
      attachments: [],
      contact_name: '',
      contact_email: '',
      contact_phone: '',
    });
    setRfqNumber('');
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => {
      if (!isOpen) resetForm();
      onOpenChange(isOpen);
    }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-purple-600" />
            Request for Quotes (RFQ)
          </DialogTitle>
        </DialogHeader>

        {/* Progress */}
        {step < 4 && (
          <div className="mb-6">
            <div className="flex justify-between mb-2">
              {steps.map((s, idx) => (
                <div 
                  key={idx}
                  className={`flex items-center gap-1 text-sm ${
                    idx <= step ? 'text-purple-600' : 'text-gray-400'
                  }`}
                >
                  <s.icon className="w-4 h-4" />
                  <span className="hidden sm:inline">{s.title}</span>
                </div>
              ))}
            </div>
            <Progress value={(step / 3) * 100} className="h-2" />
          </div>
        )}

        {/* Step 0: Basics */}
        {step === 0 && (
          <div className="space-y-4">
            <div>
              <Label htmlFor="title">RFQ Title *</Label>
              <Input
                id="title"
                value={rfqData.title}
                onChange={(e) => updateData('title', e.target.value)}
                placeholder="e.g., Industrial gloves bulk purchase"
                data-testid="rfq-title"
              />
            </div>

            <div>
              <Label>Subcategory</Label>
              <div className="p-3 bg-gray-50 rounded-lg">
                {subcategory?.subcategory_name || 'Not specified'}
              </div>
            </div>

            <div>
              <Label htmlFor="delivery_location">Delivery Location *</Label>
              <Input
                id="delivery_location"
                value={rfqData.delivery_location}
                onChange={(e) => updateData('delivery_location', e.target.value)}
                placeholder="Postcode or city"
                data-testid="rfq-location"
              />
            </div>

            <div>
              <Label htmlFor="needed_by">Needed By</Label>
              <Input
                id="needed_by"
                type="date"
                value={rfqData.needed_by}
                onChange={(e) => updateData('needed_by', e.target.value)}
                min={new Date().toISOString().split('T')[0]}
                data-testid="rfq-date"
              />
            </div>
          </div>
        )}

        {/* Step 1: Specifications */}
        {step === 1 && (
          <div className="space-y-4">
            <div className="p-3 bg-purple-50 rounded-lg text-sm text-purple-700">
              <strong>Tip:</strong> The more detail you provide, the better quotes you'll receive.
            </div>

            <div>
              <Label htmlFor="item_details">Item Details *</Label>
              <Textarea
                id="item_details"
                value={rfqData.item_details}
                onChange={(e) => updateData('item_details', e.target.value)}
                placeholder="Product name, material, grade, specifications..."
                rows={4}
                data-testid="rfq-details"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="product_type">Product Type</Label>
                <Input
                  id="product_type"
                  value={rfqData.product_type}
                  onChange={(e) => updateData('product_type', e.target.value)}
                  placeholder="e.g., Safety gloves"
                />
              </div>
              <div>
                <Label htmlFor="brand_preference">Brand Preference</Label>
                <Input
                  id="brand_preference"
                  value={rfqData.brand_preference}
                  onChange={(e) => updateData('brand_preference', e.target.value)}
                  placeholder="Optional"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="quantity">Quantity *</Label>
                <Input
                  id="quantity"
                  type="number"
                  value={rfqData.quantity}
                  onChange={(e) => updateData('quantity', e.target.value)}
                  placeholder="e.g., 500"
                  data-testid="rfq-quantity"
                />
              </div>
              <div>
                <Label htmlFor="quantity_unit">Unit</Label>
                <Select
                  value={rfqData.quantity_unit}
                  onValueChange={(value) => updateData('quantity_unit', value)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pcs">Pieces</SelectItem>
                    <SelectItem value="kg">Kilograms</SelectItem>
                    <SelectItem value="tons">Tons</SelectItem>
                    <SelectItem value="pallets">Pallets</SelectItem>
                    <SelectItem value="boxes">Boxes</SelectItem>
                    <SelectItem value="cartons">Cartons</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label htmlFor="packaging">Packaging Requirements</Label>
              <Input
                id="packaging"
                value={rfqData.packaging}
                onChange={(e) => updateData('packaging', e.target.value)}
                placeholder="e.g., Individual packs, bulk cartons"
              />
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="moq_acceptable"
                checked={rfqData.moq_acceptable}
                onChange={(e) => updateData('moq_acceptable', e.target.checked)}
                className="rounded border-gray-300"
              />
              <Label htmlFor="moq_acceptable" className="font-normal">
                MOQ (Minimum Order Quantity) acceptable
              </Label>
            </div>
          </div>
        )}

        {/* Step 2: Delivery */}
        {step === 2 && (
          <div className="space-y-4">
            <div className="flex items-center gap-4 p-4 border rounded-lg">
              <div className="flex items-center gap-2">
                <input
                  type="radio"
                  id="delivery_yes"
                  name="delivery"
                  checked={rfqData.delivery_needed}
                  onChange={() => updateData('delivery_needed', true)}
                />
                <Label htmlFor="delivery_yes" className="font-normal">Delivery needed</Label>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="radio"
                  id="delivery_no"
                  name="delivery"
                  checked={!rfqData.delivery_needed}
                  onChange={() => updateData('delivery_needed', false)}
                />
                <Label htmlFor="delivery_no" className="font-normal">I will collect</Label>
              </div>
            </div>

            {rfqData.delivery_needed ? (
              <div>
                <Label htmlFor="delivery_address">Delivery Address *</Label>
                <Textarea
                  id="delivery_address"
                  value={rfqData.delivery_address}
                  onChange={(e) => updateData('delivery_address', e.target.value)}
                  placeholder="Full delivery address including postcode"
                  rows={3}
                  data-testid="rfq-delivery-address"
                />
              </div>
            ) : (
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-600">
                  Suppliers will provide their pickup location and available time slots in their quotes.
                </p>
              </div>
            )}

            <div>
              <Label>Delivery Timeline Preference</Label>
              <Select
                value={rfqData.delivery_timeline || ''}
                onValueChange={(value) => updateData('delivery_timeline', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select preferred timeline" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="asap">As soon as possible</SelectItem>
                  <SelectItem value="within_week">Within a week</SelectItem>
                  <SelectItem value="within_2weeks">Within 2 weeks</SelectItem>
                  <SelectItem value="within_month">Within a month</SelectItem>
                  <SelectItem value="flexible">Flexible</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        )}

        {/* Step 3: Budget & Contact */}
        {step === 3 && (
          <div className="space-y-4">
            <div>
              <Label>Budget Range</Label>
              <Select
                value={rfqData.budget_range}
                onValueChange={(value) => updateData('budget_range', value)}
              >
                <SelectTrigger data-testid="rfq-budget">
                  <SelectValue placeholder="Select budget range" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="under_1000">Under {currencySymbol}1,000</SelectItem>
                  <SelectItem value="1000_5000">{currencySymbol}1,000 - {currencySymbol}5,000</SelectItem>
                  <SelectItem value="5000_10000">{currencySymbol}5,000 - {currencySymbol}10,000</SelectItem>
                  <SelectItem value="10000_50000">{currencySymbol}10,000 - {currencySymbol}50,000</SelectItem>
                  <SelectItem value="50000_plus">{currencySymbol}50,000+</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-2 p-3 bg-green-50 rounded-lg">
              <input
                type="checkbox"
                id="best_price"
                checked={rfqData.best_price_toggle}
                onChange={(e) => updateData('best_price_toggle', e.target.checked)}
                className="rounded border-gray-300"
              />
              <Label htmlFor="best_price" className="font-normal text-green-700">
                Looking for best price (suppliers will compete)
              </Label>
            </div>

            <div className="border-t pt-4">
              <h4 className="font-semibold mb-3">Your Contact Details</h4>
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="contact_name">Name *</Label>
                  <Input
                    id="contact_name"
                    value={rfqData.contact_name}
                    onChange={(e) => updateData('contact_name', e.target.value)}
                    required
                    data-testid="rfq-contact-name"
                  />
                </div>
                <div>
                  <Label htmlFor="contact_phone">Phone *</Label>
                  <Input
                    id="contact_phone"
                    value={rfqData.contact_phone}
                    onChange={(e) => updateData('contact_phone', e.target.value)}
                    required
                    data-testid="rfq-contact-phone"
                  />
                </div>
              </div>

              <div className="mt-3">
                <Label htmlFor="contact_email">Email *</Label>
                <Input
                  id="contact_email"
                  type="email"
                  value={rfqData.contact_email}
                  onChange={(e) => updateData('contact_email', e.target.value)}
                  required
                  data-testid="rfq-contact-email"
                />
              </div>
            </div>

            <div className="p-3 bg-blue-50 rounded-lg text-sm text-blue-700">
              <strong>What happens next?</strong>
              <ul className="mt-1 space-y-1 list-disc list-inside">
                <li>We'll match your RFQ with verified suppliers</li>
                <li>You'll receive up to 5 quotes within 24-48 hours</li>
                <li>Compare quotes and select your preferred supplier</li>
              </ul>
            </div>
          </div>
        )}

        {/* Step 4: Success */}
        {step === 4 && (
          <div className="text-center py-8">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <h3 className="text-2xl font-semibold text-green-700 mb-2">RFQ Submitted!</h3>
            <p className="text-gray-600 mb-4">Your RFQ reference number:</p>
            <div className="bg-gray-100 rounded-lg p-4 font-mono text-xl mb-6">
              {rfqNumber}
            </div>
            <div className="p-4 bg-purple-50 rounded-lg text-left mb-6">
              <h4 className="font-semibold text-purple-700 mb-2">Next Steps:</h4>
              <ul className="space-y-2 text-sm text-purple-600">
                <li className="flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  Quotes arriving within 24-48 hours
                </li>
                <li className="flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  Up to 5 verified suppliers will respond
                </li>
                <li className="flex items-center gap-2">
                  <MessageSquare className="w-4 h-4" />
                  You can chat with suppliers for clarifications
                </li>
              </ul>
            </div>
            <Button onClick={() => onOpenChange(false)}>
              Close
            </Button>
          </div>
        )}

        {/* Navigation */}
        {step < 4 && (
          <div className="flex justify-between pt-4 border-t">
            <Button
              variant="outline"
              onClick={() => setStep(Math.max(0, step - 1))}
              disabled={step === 0}
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              Back
            </Button>

            {step < 3 ? (
              <Button
                onClick={() => setStep(step + 1)}
                disabled={!canProceed()}
              >
                Continue
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            ) : (
              <Button
                onClick={handleSubmit}
                disabled={!canProceed() || loading}
                className="bg-purple-600 hover:bg-purple-700"
                data-testid="submit-rfq"
              >
                {loading ? 'Submitting...' : 'Submit RFQ'}
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ==================== RFQ ENTRY BUTTON/CTA ====================

export function RFQEntryButton({ 
  subcategory, 
  country = 'UK',
  variant = 'default',
  className = '',
}) {
  const [showWizard, setShowWizard] = useState(false);

  return (
    <>
      <Button
        onClick={() => setShowWizard(true)}
        className={`${variant === 'default' ? 'bg-purple-600 hover:bg-purple-700' : ''} ${className}`}
        variant={variant}
        data-testid="rfq-entry-button"
      >
        <FileText className="w-4 h-4 mr-2" />
        Request Quotes (RFQ)
      </Button>

      <RFQWizard
        open={showWizard}
        onOpenChange={setShowWizard}
        subcategory={subcategory}
        country={country}
      />
    </>
  );
}

// ==================== RFQ QUOTES COMPARISON COMPONENT ====================

export function RFQQuotesComparison({ quotes = [], onSelectQuote, currencySymbol = '£' }) {
  const [sortBy, setSortBy] = useState('price');
  const [filterVerified, setFilterVerified] = useState(false);

  const sortedQuotes = [...quotes].sort((a, b) => {
    if (sortBy === 'price') return a.price - b.price;
    if (sortBy === 'rating') return b.rating - a.rating;
    if (sortBy === 'lead_time') return a.lead_time_days - b.lead_time_days;
    return 0;
  });

  const filteredQuotes = filterVerified 
    ? sortedQuotes.filter(q => q.verified_level >= 1)
    : sortedQuotes;

  if (quotes.length === 0) {
    return (
      <div className="text-center py-12">
        <Clock className="w-12 h-12 mx-auto text-gray-300 mb-4" />
        <h3 className="font-medium text-gray-500">Waiting for quotes</h3>
        <p className="text-sm text-gray-400">Suppliers will respond within 24-48 hours</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg">
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-gray-500" />
          <span className="text-sm text-gray-600">Sort by:</span>
        </div>
        {['price', 'rating', 'lead_time'].map((option) => (
          <Button
            key={option}
            size="sm"
            variant={sortBy === option ? 'default' : 'outline'}
            onClick={() => setSortBy(option)}
            className="capitalize"
          >
            {option === 'lead_time' ? 'Lead Time' : option}
          </Button>
        ))}
        <div className="flex items-center gap-2 ml-auto">
          <input
            type="checkbox"
            id="verified_only"
            checked={filterVerified}
            onChange={(e) => setFilterVerified(e.target.checked)}
            className="rounded"
          />
          <Label htmlFor="verified_only" className="text-sm font-normal">
            Verified only
          </Label>
        </div>
      </div>

      {/* Quotes Table */}
      <div className="border rounded-lg overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="text-left p-3 text-sm font-medium text-gray-600">Supplier</th>
              <th className="text-left p-3 text-sm font-medium text-gray-600">Price</th>
              <th className="text-left p-3 text-sm font-medium text-gray-600">Lead Time</th>
              <th className="text-left p-3 text-sm font-medium text-gray-600">Delivery</th>
              <th className="text-left p-3 text-sm font-medium text-gray-600">Rating</th>
              <th className="text-left p-3 text-sm font-medium text-gray-600">Action</th>
            </tr>
          </thead>
          <tbody>
            {filteredQuotes.map((quote) => (
              <tr key={quote.id} className="border-t hover:bg-gray-50">
                <td className="p-3">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{quote.supplier_name}</span>
                    {quote.verified_level > 0 && (
                      <Badge className="bg-green-100 text-green-700 text-xs">
                        <Shield className="w-3 h-3 mr-1" />
                        L{quote.verified_level}
                      </Badge>
                    )}
                  </div>
                </td>
                <td className="p-3 font-semibold">{currencySymbol}{quote.price?.toLocaleString()}</td>
                <td className="p-3">{quote.lead_time_days} days</td>
                <td className="p-3">
                  <Badge variant={quote.delivery_included ? 'default' : 'outline'} className="text-xs">
                    {quote.delivery_included ? 'Included' : 'Extra'}
                  </Badge>
                </td>
                <td className="p-3">
                  <div className="flex items-center gap-1">
                    <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                    <span>{quote.rating?.toFixed(1)}</span>
                  </div>
                </td>
                <td className="p-3">
                  <Button size="sm" onClick={() => onSelectQuote?.(quote)}>
                    Compare
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default RFQWizard;
